<template>
  <div class="login-page">
    <div class="login-header">
      <button
        @click="$router.push({ name: 'welcome' })"
        class="btn btn-secondary btn-back"
      >
        <i class="bi bi-arrow-left"></i>
        <span>Retour</span>
      </button>
    </div>

    <div class="login-content">
      <div class="login-form">
        <div class="form-header">
          <div class="form-logo">
            <img src="/logo.png" alt="Peascom Caisse Manager" />
          </div>
          <h2 class="form-title">Connexion</h2>
          <p class="form-subtitle">Connectez-vous Ã  votre compte</p>
        </div>

        <div class="form-body">
          <div v-if="savedAccountInfo" class="saved-account-banner">
            <div class="saved-account-info">
              <div class="saved-account-icon">
                <i class="bi bi-person-circle"></i>
              </div>
              <div class="saved-account-details">
                <p class="saved-account-name">
                  {{
                    savedAccountInfo.fullName ||
                    savedAccountInfo.full_name ||
                    "Utilisateur"
                  }}
                </p>
                <p class="saved-account-phone">
                  {{ savedAccountInfo.username || savedAccountInfo.phone || "" }}
                </p>
              </div>
            </div>
            <button
              type="button"
              @click="changeUser"
              class="btn btn-change-user"
              :disabled="isLoggingIn"
            >
              <i class="bi bi-arrow-repeat"></i>
              <span>Changer d'utilisateur</span>
            </button>
          </div>


          <div class="form-group" v-if="!savedAccountInfo">
            <label for="identifier" class="form-label">
              <i class="bi bi-person"></i>
              Nom d'utilisateur ou telephone
            </label>
            <input
              id="identifier"
              type="text"
              v-model="phone"
              class="form-input"
              placeholder="Entrez votre nom d'utilisateur ou telephone"
              @keyup.enter="handleLogin"
              :disabled="isLoggingIn"
              :class="{ 'has-error': errors.phone }"
            />
            <p v-if="errors.phone" class="error-text">{{ errors.phone }}</p>
          </div>

          <div class="form-group">
            <label for="password" class="form-label">
              <i class="bi bi-lock"></i>
              Mot de passe
            </label>
            <div class="password-input-wrapper">
              <input
                id="password"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                class="form-input"
                placeholder="Entrez votre mot de passe"
                @keyup.enter="handleLogin"
                :disabled="isLoggingIn"
                :class="{ 'has-error': errors.password }"
              />
              <button
                type="button"
                class="btn btn-toggle-password"
                @click="showPassword = !showPassword"
                tabindex="-1"
              >
                <i v-if="showPassword" class="bi bi-eye-slash-fill"></i>
                <i v-else class="bi bi-eye-fill"></i>
              </button>
            </div>
            <p v-if="errors.password" class="error-text">
              {{ errors.password }}
            </p>
          </div>

          <button
            type="button"
            @click="handleLogin"
            class="btn btn-primary btn-login"
            :disabled="
              isLoggingIn || (!savedAccountInfo && !phone) || !password
            "
          >
            <span v-if="isLoggingIn" class="btn-content">
              <i class="bi bi-arrow-repeat spin"></i>
              Connexion en cours...
            </span>
            <span v-else class="btn-content">
              <span>Se connecter</span>
              <i class="bi bi-arrow-right"></i>
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Login",
  data() {
    return {
      phone: "",
      password: "",
      showPassword: false,
      isLoggingIn: false,
      errors: {},
      deviceIp: null,
      deviceInfo: null,
      savedAccountInfo: null,
    };
  },
  mounted() {
    this.loadSavedAccountInfo();
    this.getDeviceInfo();
    this.getDeviceIp();
  },
  methods: {
    loadSavedAccountInfo() {
      try {
        const savedInfo = localStorage.getItem("saved-account-info");
        if (savedInfo) {
          this.savedAccountInfo = JSON.parse(savedInfo);
          // Auto-populate saved identifier for login
          const savedIdentifier =
            this.savedAccountInfo.username || this.savedAccountInfo.phone;
          if (savedIdentifier) {
            this.phone = savedIdentifier;
          }
        } else {
          this.savedAccountInfo = null;
        }
      } catch (error) {
        console.error("Error loading saved account info:", error);
        this.savedAccountInfo = null;
        localStorage.removeItem("saved-account-info");
      }
    },
    saveAccountInfo(user) {
      try {
        const accountInfo = {
          username: user.username || this.phone,
          phone: user.phone || this.phone,
          fullName: user.fullName || user.full_name || user.name || "",
        };
        localStorage.setItem("saved-account-info", JSON.stringify(accountInfo));
        this.savedAccountInfo = accountInfo;
      } catch (error) {
        console.error("Error saving account info:", error);
      }
    },
    clearSavedAccount() {
      localStorage.removeItem("saved-account-info");
      this.savedAccountInfo = null;
      this.phone = "";
      this.password = "";
      this.errors = {};
    },
    changeUser() {
      this.clearSavedAccount();
      // Focus on identifier input after clearing
      this.$nextTick(() => {
        const phoneInput = document.getElementById("identifier");
        if (phoneInput) {
          phoneInput.focus();
        }
      });
    },
    getDeviceInfo() {
      const navigator = window.navigator;
      this.deviceInfo = {
        userAgent: navigator.userAgent || "Unknown",
        platform: navigator.platform || "Unknown",
        language: navigator.language || "Unknown",
        vendor: navigator.vendor || "Unknown",
        screenWidth: window.screen?.width || 0,
        screenHeight: window.screen?.height || 0,
        screenColorDepth: window.screen?.colorDepth || 0,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown",
        cookieEnabled: navigator.cookieEnabled || false,
        onLine: navigator.onLine || false,
        hardwareConcurrency: navigator.hardwareConcurrency || 0,
        deviceMemory: navigator.deviceMemory || 0,
      };
    },
    async getDeviceIp() {
      try {
        // Helper function to fetch with timeout
        const fetchWithTimeout = (url, timeout = 5000) => {
          return Promise.race([
            fetch(url),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Timeout")), timeout)
            ),
          ]);
        };

        // Try multiple IP detection services as fallback
        const ipServices = [
          {
            url: "https://api.ipify.org?format=json",
            parser: (data) => data.ip,
          },
          {
            url: "https://api.ip.sb/ip",
            parser: (data) =>
              typeof data === "string" ? data.trim() : data.ip,
          },
          { url: "https://api.myip.com", parser: (data) => data.ip },
          { url: "https://ipapi.co/json/", parser: (data) => data.ip },
        ];

        for (const service of ipServices) {
          try {
            const response = await fetchWithTimeout(service.url, 5000);
            if (!response.ok) continue;

            let data;
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              data = await response.json();
            } else {
              const text = await response.text();
              // Try to parse as JSON, otherwise use as string
              try {
                data = JSON.parse(text);
              } catch {
                data = text;
              }
            }

            // Use the parser function or default logic
            this.deviceIp = service.parser
              ? service.parser(data)
              : data.ip || data.query || data || null;

            if (
              this.deviceIp &&
              this.deviceIp !== "Unknown" &&
              this.deviceIp.trim() !== ""
            ) {
              break;
            }
          } catch (error) {
            console.warn(
              `Failed to get IP from ${service.url}:`,
              error.message
            );
            continue;
          }
        }

        // Fallback to "Unknown" if all services fail
        if (!this.deviceIp || this.deviceIp === "") {
          this.deviceIp = "Unknown";
        }
      } catch (error) {
        console.error("Error getting device IP:", error);
        this.deviceIp = "Unknown";
      }
    },
    validate() {
      this.errors = {};

      // If saved account info exists, use its identifier, otherwise validate input
      const phoneToValidate = this.savedAccountInfo
        ? this.savedAccountInfo.username || this.savedAccountInfo.phone
        : this.phone;

      if (!phoneToValidate || !phoneToValidate.trim()) {
        this.errors.phone = "L'identifiant est requis.";
      } else if (!/^[a-zA-Z0-9_@.+\s-]+$/.test(phoneToValidate.trim())) {
        this.errors.phone = "Veuillez entrer un identifiant valide.";
      }

      if (!this.password || !this.password.trim()) {
        this.errors.password = "Le mot de passe est requis.";
      }

      return Object.keys(this.errors).length === 0;
    },
    async handleLogin() {
      // Prevent multiple simultaneous login requests
      if (this.isLoggingIn) {
        return;
      }

      // Validate inputs
      if (!this.validate()) {
        return;
      }

      // Attempt login with error handling
      try {
        this.isLoggingIn = true;
        this.errors = {};

        // Use identifier from saved account info or input
        const identifierToUse = this.savedAccountInfo
          ? this.savedAccountInfo.username || this.savedAccountInfo.phone
          : this.phone.trim();

        const loginResponse = await this.$store.dispatch("auth/login", {
          password: this.password.trim(),
          username: identifierToUse,
          phone: identifierToUse,
          ip_address: this.deviceIp || "Unknown",
          device_info: this.deviceInfo || {},
        });

        // Save account info after successful login
        if (loginResponse && loginResponse.data && loginResponse.data.user) {
          this.saveAccountInfo(loginResponse.data.user);
        } else {
          // Fallback: get user from store
          const user = this.$store.getters["auth/user"];
          if (user && (user.id || user.reference)) {
            this.saveAccountInfo(user);
          }
        }

        // If login succeeds, router will navigate away automatically
        // No need to reset isLoggingIn as component will be destroyed
      } catch (error) {
        console.error("Login error:", error);
        // Reset flag on error so user can try again
        this.isLoggingIn = false;

        // Show error message to user if toast is available
        if (this.$toast) {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "NumÃ©ro de tÃ©lÃ©phone ou mot de passe incorrect. Veuillez rÃ©essayer.";
          this.$toast.error(errorMessage);
        } else {
          // Fallback error display
          this.errors.password =
            error.response?.data?.message ||
            "NumÃ©ro de tÃ©lÃ©phone ou mot de passe incorrect.";
        }

        // Clear password on error so user can retry
        this.password = "";
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: #ffffff;
  padding: 2rem;
  display: flex;
  flex-direction: column;
}

.login-header {
  max-width: 500px;
  margin: 0 auto 2rem;
  width: 100%;
}

.btn-back {
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-back:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.login-content {
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-form {
  background: #ffffff;
  border-radius: 24px;
  padding: 3rem 2.5rem;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
}

.form-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.form-logo {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 20px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  padding: 1rem;
  box-shadow: 0 10px 30px rgba(99, 102, 241, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.form-title {
  font-size: 2rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;
}

.form-subtitle {
  font-size: 1rem;
  color: #64748b;
  margin: 0;
}

.form-body {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.saved-account-banner {
  background: linear-gradient(135deg, #f0f4ff 0%, #eef2ff 100%);
  border: 2px solid #c7d2fe;
  border-radius: 16px;
  padding: 1.25rem;
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.saved-account-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.saved-account-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.saved-account-details {
  flex: 1;
  min-width: 0;
}

.saved-account-name {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.25rem 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.saved-account-phone {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.btn-change-user {
  width: 100%;
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  border: 1px solid #c7d2fe;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #ffffff;
  color: #6366f1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-change-user:hover:not(:disabled) {
  background: #f0f4ff;
  border-color: #a5b4fc;
  color: #4f46e5;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
}

.btn-change-user:active:not(:disabled) {
  transform: translateY(0);
}

.btn-change-user:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-change-user i {
  font-size: 1rem;
  transition: transform 0.3s ease;
}

.btn-change-user:hover:not(:disabled) i {
  transform: rotate(180deg);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-label i {
  color: #6366f1;
  font-size: 1rem;
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  color: #1e293b;
  background: #ffffff;
  transition: all 0.3s ease;
}

.form-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
  opacity: 0.7;
}

.form-input.has-error {
  border-color: #ef4444;
}

.form-input.has-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper .form-input {
  padding-right: 3rem;
}

.btn-toggle-password {
  position: absolute;
  right: 0.75rem;
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  z-index: 1;
}

.btn-toggle-password:hover {
  color: #6366f1;
}

.error-text {
  font-size: 0.875rem;
  color: #ef4444;
  margin: 0;
}

.btn-login {
  width: 100%;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  box-shadow: 0 10px 30px rgba(99, 102, 241, 0.3);
  margin-top: 0.5rem;
}

.btn-login:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 15px 40px rgba(99, 102, 241, 0.4);
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
}

.btn-login:active:not(:disabled) {
  transform: translateY(0);
}

.btn-login:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.form-input:disabled {
  background: #f8fafc;
  color: #64748b;
  cursor: not-allowed;
  opacity: 0.8;
}

.btn-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-content i {
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}

.btn-login:hover:not(:disabled) .btn-content i {
  transform: translateX(4px);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media screen and (max-width: 768px) {
  .login-page {
    padding: 1rem;
  }

  .login-form {
    padding: 2rem 1.5rem;
  }

  .form-title {
    font-size: 1.75rem;
  }

  .form-logo {
    width: 70px;
    height: 70px;
  }

  .saved-account-banner {
    padding: 1rem;
  }

  .saved-account-icon {
    width: 44px;
    height: 44px;
    font-size: 1.35rem;
  }

  .saved-account-name {
    font-size: 0.95rem;
  }

  .saved-account-phone {
    font-size: 0.8rem;
  }
}

@media screen and (max-width: 480px) {
  .login-form {
    padding: 1.5rem 1.25rem;
  }

  .form-title {
    font-size: 1.5rem;
  }

  .saved-account-banner {
    padding: 0.875rem;
    gap: 0.875rem;
  }

  .saved-account-info {
    gap: 0.875rem;
  }

  .saved-account-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .saved-account-name {
    font-size: 0.9rem;
  }

  .saved-account-phone {
    font-size: 0.75rem;
  }

  .btn-change-user {
    padding: 0.625rem 1rem;
    font-size: 0.85rem;
  }
}
</style>
